import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
    Form,
    Input,
    Button,
    InputNumber,
    Select,
    Card,
    Typography,
    Space,
    message,
    Spin,
    Divider,
    Modal,
    Steps,
    Radio,
    Row,
    Col,
    Tooltip,
    Tag,
    Collapse,
    Checkbox,
    Alert,
    Descriptions,
    Skeleton
} from 'antd';
import {
    PlusOutlined,
    MinusCircleOutlined,
    CheckCircleOutlined,
    PlusCircleOutlined,
    ShoppingOutlined,
    FileTextOutlined,
    MobileOutlined,
    HomeOutlined,
    PictureOutlined,
    CrownOutlined,
    GiftOutlined,
    CarOutlined,
    BankOutlined,
    HistoryOutlined,
    HeartOutlined,
    TeamOutlined,
    FireOutlined,
    CloudOutlined,
    SearchOutlined,
    AppstoreOutlined,
    InfoCircleOutlined,
    SaveOutlined,
    DeleteOutlined,
    EditOutlined,
    DragOutlined,
    CalendarOutlined,
    EnvironmentOutlined,
    LockOutlined,
    UnlockOutlined,
    GlobalOutlined,
    OrderedListOutlined,
    VideoCameraOutlined
} from '@ant-design/icons';
import auctionService from '../../services/auctionService';
import sampleTemplates from '../../data/SampleLotData.json';
import specificationData from '../../data/specificationData.json';
import DashboardLayout from '../Dashboard/DashboardLayout';
import LotForm from './LotForm';
import SpecificationGroup from './SpecificationGroup';
import moment from 'moment';
const { Title } = Typography;
const { TextArea } = Input;

const CreateEditLot = () => {
    const [form] = Form.useForm();
    const navigate = useNavigate();
    const { auctionSlug, lotSlug } = useParams();
    const location = useLocation();
    const [loading, setLoading] = useState(false);
    const [auctions, setAuctions] = useState([]);
    const [successModalVisible, setSuccessModalVisible] = useState(false);
    const [newLotSlug, setNewLotSlug] = useState(null);
    const [lotAuctionStatus, setLotAuctionStatus] = useState(null);
    const isEdit = !!lotSlug;

    // Get auction_slug from URL query parameters
    const queryParams = new URLSearchParams(location.search);
    const auctionSlugFromUrl = queryParams.get('auction_slug');

    useEffect(() => {
        loadInitialData();
    }, [auctionSlug, lotSlug, auctionSlugFromUrl]);

    const loadInitialData = async () => {
        try {
            setLoading(true);
            // When creating a new lot, exclude completed auctions from dropdown
            // When editing, include all auctions (including completed) so we can still see/edit the lot
            const promises = [isEdit 
                ? auctionService.getAuctionsForDropdown() 
                : auctionService.getAuctionsForDropdownActive()];
            
            if (isEdit) {
                promises.push(auctionService.getLot(auctionSlug, lotSlug));
            }

            const [auctionsResponse, lotResponse] = await Promise.all(promises);
            
            if (auctionsResponse?.data) {
                setAuctions(auctionsResponse.data);
            }

            if (isEdit && lotResponse?.data) {
                const lot = lotResponse.data;
                
                // Store auction status to check if auction is completed
                if (lot.auction?.status) {
                    setLotAuctionStatus(lot.auction.status);
                }
                
                // Format specifications for the form
                const specifications = lot.specifications?.map(group => ({
                    heading: group.heading,
                    fields: group.fields.map(field => {
                        const [key, value] = Object.entries(field)[0];
                        return { key, value };
                    })
                })) || [];

                // Determine if reserve is enabled
                const hasReserve = lot.is_reserve || !!lot.reserve_price;
                
                // Set form values
                const formValues = {
                    auction_id: lot.auction_id || lot.auction?.id,
                    title: lot.title,
                    description: lot.description,
                    starting_price: parseFloat(lot.starting_price),
                    is_reserve: hasReserve,
                    reserve_price: lot.reserve_price ? parseFloat(lot.reserve_price) : undefined,
                    quantity: lot.quantity,
                    default_increment: lot.default_increment || lot.increment || 5,
                    specifications: specifications.length > 0 ? specifications : [{ heading: '', fields: [{ key: '', value: '' }] }],
                    video_url: lot.video_url
                };
                
                // Only set Buy Now values if there's no reserve price
                // This ensures Buy Now fields show when editing lots without reserve
                if (!hasReserve) {
                    formValues.can_buy_now = lot.can_buy_now || false;
                    formValues.buy_now_price = lot.buy_now_price ? parseFloat(lot.buy_now_price) : undefined;
                } else {
                    // If reserve exists, clear Buy Now (they're mutually exclusive)
                    formValues.can_buy_now = false;
                    formValues.buy_now_price = undefined;
                }
                
                console.log('Setting form values for edit:', {
                    hasReserve,
                    can_buy_now: formValues.can_buy_now,
                    buy_now_price: formValues.buy_now_price,
                    lot_can_buy_now: lot.can_buy_now,
                    lot_buy_now_price: lot.buy_now_price
                });
                
                form.setFieldsValue(formValues);
            } else if (auctionSlugFromUrl) {
                // Find the auction in the dropdown data
                const auction = auctionsResponse.data.find(a => a.slug === auctionSlugFromUrl);
                if (auction) {
                    form.setFieldValue('auction_id', auction.id);
                }
            }
        } catch (error) {
            console.error('Error loading initial data:', error);
            message.error('Failed to load data. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (values) => {
        try {
            setLoading(true);
            
            // Format specifications for the backend
            const specifications = values.specifications.map(group => ({
                heading: group.heading,
                fields: group.fields.map(field => ({
                    [field.key]: field.value
                }))
            }));

            const formData = {
                ...values,
                specifications
            };

            if (isEdit) {
                const response = await auctionService.updateLot(auctionSlug, lotSlug, formData);
                message.success('Lot updated successfully');
                // Use the updated slug from response (in case title was changed and slug regenerated)
                const updatedLotSlug = response?.data?.slug || lotSlug;
                navigate(`/auctions/${auctionSlug}/lots/${updatedLotSlug}/images`);
            } else {
                const auction = auctions.find(a => a.id === values.auction_id);
                if (!auction) {
                    message.error('Selected auction not found');
                    return;
                }
                const response = await auctionService.createLot(auction.slug, formData);
                if (response?.data?.slug) {
                    setNewLotSlug(response.data.slug);
                    setSuccessModalVisible(true);
                } else {
                    throw new Error('Failed to create lot: No slug returned');
                }
            }
        } catch (error) {
            console.error('Error saving lot:', error);
            message.error(error.response?.data?.message || 'Failed to save lot');
        } finally {
            setLoading(false);
        }
    };

    const handleSuccessModalClose = () => {
        setSuccessModalVisible(false);
        const auction = auctions.find(a => a.id === form.getFieldValue('auction_id'));
        navigate(`/auctions/${auction.slug}/lots/${newLotSlug}/images`);
    };

    const handleAuctionChange = (auctionId) => {
        if (!isEdit && auctionId) {
            const selectedAuction = auctions.find(a => a.id === auctionId);
            if (selectedAuction && selectedAuction.category) {
                // Load specifications based on category
                loadSpecificationsByCategory(selectedAuction.category.slug || selectedAuction.category.name, false);
            }
        }
    };

    const handleManualLoadSpecifications = (auctionId) => {
        if (!isEdit && auctionId) {
            const selectedAuction = auctions.find(a => a.id === auctionId);
            if (selectedAuction && selectedAuction.category) {
                // Load specifications based on category with manual flag
                loadSpecificationsByCategory(selectedAuction.category.slug || selectedAuction.category.name, true);
            } else {
                message.warning('Please select an auction with a category to load specifications.');
            }
        }
    };

    const loadSpecificationsByCategory = (categorySlug, isManual = false) => {
        // Map normalized category names to specification data keys
        const categoryMapping = {
            horse: 'horse',
            sheep: 'sheep',
            goat: 'goat',
            buffalo: 'buffalo',
            cattle: 'cattle',
            poultry: 'poultry',
            machinery: 'machinery',
            property: 'property',
            fodder_seeds_trees: 'fodder_seeds_trees',
            feed_fodder: 'feed_fodder',
            genetic_materials: 'genetic_materials'
        };
        const normalizedCategory = categorySlug.toLowerCase().replace(/\s+/g, '_');
        const specKey = categoryMapping[normalizedCategory];

        if (specKey && specificationData.specifications[specKey]) {
            const specifications = specificationData.specifications[specKey].specifications.map(group => ({
                heading: group.heading,
                fields: group.fields.map(field => ({
                    key: field.key,
                    value: field.value
                }))
            }));

            form.setFieldsValue({ specifications });
            
            if (isManual) {
                message.success(`✅ Loaded ${specificationData.specifications[specKey].name} specifications successfully!`);
            } else {
                message.success(`📋 Auto-loaded ${specificationData.specifications[specKey].name} specifications`);
            }
        } else {
            if (isManual) {
                message.warning(`⚠️ No specification template found for category: ${categorySlug}`);
            }
        }
    };

    const renderSuccessModal = () => {
        return (
            <Modal
                title={
                    <Space size="middle">
                        <div style={{
                            background: 'linear-gradient(135deg, #52c41a 0%, #389e0d 100%)',
                            padding: '8px',
                            borderRadius: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}>
                            <CheckCircleOutlined style={{ color: 'white', fontSize: '24px' }} />
                        </div>
                        <span style={{ fontSize: '20px', fontWeight: 600, color: '#1a1a1a' }}>
                            🎉 Lot Created Successfully!
                        </span>
                    </Space>
                }
                open={successModalVisible}
                onCancel={handleSuccessModalClose}
                footer={[
                    <Button 
                        key="continue" 
                        type="primary" 
                        onClick={handleSuccessModalClose}
                        size="large"
                        style={{ 
                            borderRadius: '8px',
                            minWidth: '200px',
                            height: '44px',
                            fontWeight: 500,
                            boxShadow: '0 2px 8px rgba(24, 144, 255, 0.3)'
                        }}
                    >
                        📸 Continue to Add Images
                    </Button>
                ]}
                width={700}
                style={{ borderRadius: '16px' }}
            >
                <div style={{ padding: '20px 0' }}>
                    <Typography.Paragraph style={{ fontSize: '16px', marginBottom: 28, color: '#595959', lineHeight: '1.6' }}>
                        <strong style={{ color: '#52c41a' }}>Congratulations!</strong> Your lot has been created successfully. 
                        Here's what you can do next to make it even better:
                    </Typography.Paragraph>

                    <Steps
                        direction="vertical"
                        current={0}
                        items={[
                            {
                                title: <span style={{ fontWeight: 500, fontSize: '16px' }}>Add Lot Images</span>,
                                description: (
                                    <span style={{ fontSize: '14px', color: '#8c8c8c' }}>
                                        Upload high-quality images of your lot items. Clear, detailed photos help attract more bidders and increase confidence in your listing.
                                    </span>
                                ),
                                icon: <PictureOutlined style={{ color: '#1890ff', fontSize: '20px' }} />
                            }
                        ]}
                        style={{ marginBottom: 24 }}
                    />

                    <div style={{ 
                        marginTop: 24, 
                        padding: 20, 
                        backgroundColor: '#f6ffed', 
                        border: '2px solid #b7eb8f',
                        borderRadius: '12px',
                        boxShadow: '0 2px 8px rgba(82, 196, 26, 0.1)'
                    }}>
                        <Space size="middle" align="start">
                            <InfoCircleOutlined style={{ color: '#52c41a', fontSize: '20px', marginTop: 2 }} />
                            <Typography.Text style={{ fontSize: '15px', color: '#595959', lineHeight: '1.6' }}>
                                <strong style={{ color: '#389e0d' }}>Ready to add images?</strong> Click "Continue to Add Images" 
                                to start uploading photos that showcase your lot in the best light!
                            </Typography.Text>
                        </Space>
                    </div>
                </div>
            </Modal>
        );
    };

    const isAuctionCompleted = lotAuctionStatus === 'completed';
    const isAuctionCancelled = lotAuctionStatus === 'cancelled';
    const isUpdateDisabled = isEdit && (isAuctionCompleted || isAuctionCancelled);

    return (
        <DashboardLayout className="create-edit-lot">
                <div style={{ marginBottom: 32 }}>
                    <div style={{ marginBottom: 16 }}>
                        <Title level={2} style={{ margin: 0, color: '#1a1a1a', fontWeight: 600 }}>
                            {isEdit ? '✏️ Edit Lot' : '✨ Create New Lot'}
                        </Title>
                        <Typography.Paragraph style={{ color: '#595959', fontSize: '16px', marginTop: 8, marginBottom: 0 }}>
                            {isEdit 
                                ? 'Update your lot details and specifications. Make any changes you need below.'
                                : 'Welcome! Let\'s create an amazing lot together. Fill in the details below, and you can add images right after.'}
                        </Typography.Paragraph>
                    </div>
                    {isEdit && isAuctionCompleted && (
                        <Alert
                            message={<span style={{ fontWeight: 500 }}>Auction Completed</span>}
                            description="This auction has already been completed. You cannot update lots in completed auctions."
                            type="warning"
                            showIcon
                            style={{ 
                                marginTop: 16,
                                borderRadius: '8px',
                                border: '2px solid #ffe58f'
                            }}
                        />
                    )}
                    {isEdit && isAuctionCancelled && (
                        <Alert
                            message={<span style={{ fontWeight: 500 }}>Auction Cancelled</span>}
                            description="This auction has been cancelled. You cannot update lots in cancelled auctions."
                            type="warning"
                            showIcon
                            style={{ 
                                marginTop: 16,
                                borderRadius: '8px',
                                border: '2px solid #ffe58f'
                            }}
                        />
                    )}
                </div>

                <LotForm
                    form={form}
                    loading={loading}
                    isEdit={isEdit}
                    auctions={auctions}
                    auctionIdFromUrl={auctionSlugFromUrl}
                    onAuctionChange={handleAuctionChange}
                    onManualLoadSpecifications={handleManualLoadSpecifications}
                    renderSpecificationGroup={(field, { add, remove }, { errors }) => (
                        <SpecificationGroup
                            fields={field}
                            add={add}
                            remove={remove}
                            errors={errors}
                        />
                    )}
                    onSubmit={handleSubmit}
                    disabled={isUpdateDisabled}
                />
                <div style={{ 
                    marginTop: 40,
                    paddingTop: 32,
                    borderTop: '2px solid #f0f0f0',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 16
                }}>
                    <div>
                        <Typography.Text type="secondary" style={{ fontSize: '14px' }}>
                            {isEdit 
                                ? '💡 Make your changes and click "Update Lot" when you\'re ready.'
                                : '💡 Review your details and click "Create Lot" when everything looks good!'}
                        </Typography.Text>
                    </div>
                    <Space size="middle">
                        <Button 
                            onClick={() => window.history.back()}
                            size="large"
                            style={{ borderRadius: '8px', minWidth: '100px' }}
                        >
                            Cancel
                        </Button>
                        <Button 
                            type="primary" 
                            onClick={() => form.submit()} 
                            loading={loading}
                            disabled={isUpdateDisabled}
                            size="large"
                            style={{ 
                                borderRadius: '8px',
                                minWidth: '140px',
                                height: '40px',
                                fontWeight: 500,
                                boxShadow: '0 2px 8px rgba(24, 144, 255, 0.3)'
                            }}
                        >
                            {loading ? 'Processing...' : (isEdit ? '✅ Update Lot' : '✨ Create Lot')}
                        </Button>
                    </Space>
                </div>

            {renderSuccessModal()}
        </DashboardLayout>
    );
};

export default CreateEditLot; 